import { SECTIONS } from "../../../../config/constants";
import PageHead from "../../../layout/PageHead";
import {
  Tabs,
  Select,
  Button,
  Form,
  Input,
  Checkbox,
  Drawer,
  Space,
  DrawerProps,
  notification,
  Popover,
  Collapse,
} from "antd";
import "./UserManagement.css";
import {
  UserSwitchOutlined,
  EditOutlined,
  GroupOutlined,
  CloseOutlined,
  CheckOutlined,
  PlusOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import GroupCodesService from "../../../../services/groupcodes/GroupCodesService";
import TablesService from "../../../../services/tables/TablesService";
import Datatable from "../../../shared/components/Datatable";
import { ColumnsType } from "antd/lib/table";
import UsersService from "../../../../services/admin/UsersService";
import UserGroupService from "../../../../services/admin/UserGroupsService";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { NotificationPlacement } from "antd/lib/notification";
import { CreateUser } from "../../../../models/admin/CreateUser";
import { UpdateUser } from "../../../../models/admin/UpdateUser";
import { useTranslation } from "react-i18next";
import styles from "../user-management/UserManagement.module.css";
import PermissionsService from "../../../../services/admin/PermissionsService";
import { UpsertPermission } from "../../../../models/permissions/UpsertPermission";
import { useSelector } from "react-redux";
import {PERMISSION_METHODS_ARRAY} from "../../../../config/consts/permissions";
import {ROUTES} from "../../../../config/consts/routes";

const { TabPane } = Tabs;
const { Panel } = Collapse;

const routes = [
  {
    path: ROUTES.general.main.route,
    breadcrumbName: SECTIONS.general.name,
  },
  {
    breadcrumbName: SECTIONS.administration.name,
  },
  {
    path: ROUTES.administration.userManagement.route,
    breadcrumbName: ROUTES.administration.userManagement.name,
  },
];

enum RowAction {
  View = 1,
  Edit,
  Create,
}

const UserManagement = () => {
  const { t, i18n } = useTranslation("translations");

  const [groupCodes, setGroupCodes] = useState<any>([]);
  const [entities, setEntities] = useState<any>([]);
  const [operations, setOperations] = useState<any>([]);
  const [selectedGroupCode, setSelectedGroupCode] = useState<any>();
  const [selectedEntity, setSelectedEntity] = useState<any>();
  const [selectedOperation, setSelectedOperation] = useState<any>();
  const [data, setData] = useState<DataType[]>([]);
  const [isTableLoading, setIsTableLoading] = useState(true);
  const [drawerAction, setDrawerAction] = useState<RowAction>();
  const [drawerTitle, setDrawerTitle] = useState<string>("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [placement, setPlacement] = useState<DrawerProps["placement"]>("bottom");
  const [loadingButton, setLoadingButton] = useState(false);
  const [userNameInput, setUserNameInput] = useState("");
  const [userTypeInput, setUserTypeInput] = useState<number>(1);
  const [emailInput, setEmailInput] = useState("");
  const [azureAdId, setAzureAdId] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [isSocial, setIsSocial] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editedRow, setEditedRow] = useState<any>();
  const [updateRowAllowed, setUpdateRowAllowed] = useState(false);
  const [groupsInput, setGroupsInput] = useState<any>([]);
  const [groups, setGroups] = useState<any>([]);
  const [dbUserGroups, setDbUserGroups] = useState<any>([]);
  const [selectedGroup, setSelectedGroup] = useState<any>({});
  const [activeTab, setActiveTab] = useState("user-management");
  const [entityPermissionsData, setEntityPermissionsData] = useState<any[]>([]);
  const [groupCodePermissionsData, setGroupCodePermissionsData] = useState<any[]>([]);
  const [dbEntityPermissionsData, setDbEntityPermissionsData] = useState<any[]>([]);
  const [dbGroupCodePermissionsData, setDbGroupCodePermissionsData] = useState<any[]>([]);

  const [editAllowed, setEditAllowed] = useState<boolean>(false);

  const { profile } = useSelector((state: any) => ({
    profile: state.profile,
  }));

  interface DataType {
    id: number;
    email: string;
    userName: string;
    sysTimeCreated: string;
    sysTimeUpdated: string;
    sysUserCreated: string;
    sysUserUpdated: string;
    isSocial: boolean;
    isAdmin: boolean;
    password: string;
    userGroups: UserGroups[];
  }

  interface UserGroups {
    id: number;
    name: string;
    level: number;
    sysTimeCreated: string;
    sysTimeUpdated: string;
    sysUserCreated: string;
    sysUserUpdated: string;
  }

  interface EditGroupsDataType {
    name: string;
    level: string;
  }

  enum UserType {
    Local = 1,
    AzureAD = 2,
  }

  useEffect(() => {
    document.title = ROUTES.administration.userManagement.name;
  }, []);

  useEffect(() => {
    UsersService.permissions(profile.id)
      .then((response) => {
        setEditAllowed(response.data.isAdmin);
      })
  }, [profile])

  const showDrawer = (action: RowAction) => {
    setDrawerAction(action);
    switch (action) {
      case RowAction.View:
        setDrawerTitle("Row details");
        break;
      case RowAction.Create:
        setEmailInput("");
        setUserNameInput("");
        setPasswordInput("");
        setUserTypeInput(UserType.Local)
        setIsSocial(false);
        setIsAdmin(false);
        setGroupsInput([]);
        setDrawerTitle("New user");
        break;
      case RowAction.Edit:
        setDrawerTitle("Edit user");
        break;
      default:
        break;
    }
    setOpenDrawer(true);
  };

  const onClose = () => {
    setOpenDrawer(false);
    setEmailInput("");
    setUserNameInput("");
    setPasswordInput("");
    setIsSocial(false);
    setIsAdmin(false);
    setGroupsInput([]);
    setEditedRow(null);
    setGroupCodePermissionsData([]);
    setEntityPermissionsData([]);
    setDbGroupCodePermissionsData([]);
    setDbEntityPermissionsData([]);
  };

  const getUserPermissions = (userId: number) => {
    PermissionsService.get(null, userId, null).then((response) => {
      const groupCodePermissions = response.data.filter((p) => p.node === "GROUPCODE");
      const entityPermissions = response.data.filter((p) => p.node === "ENTITY");

      const groupCodePermissionsMap = groupCodes.map((gc) => {
        const permission = groupCodePermissions.find((p) => p.fkNode === gc.id);

        return {
          valueId: gc.id,
          valueName: gc.code,
          id: permission?.id ?? -1,
          object: "USER",
          objectId: userId,
          isAllowed: permission?.isAllowed,
          node: "GROUPCODE",
          method: permission?.value ?? "NOT ASSIGNED",
        };
      });

      const enitityPermissionsMap = entities.map((e) => {
        const permission = entityPermissions.find((p) => p.fkNode === e.id);

        return {
          valueId: e.id,
          valueName: e.name,
          id: permission?.id ?? -1,
          object: "USER",
          objectId: userId,
          isAllowed: permission?.isAllowed,
          node: "ENTITY",
          method: permission?.value ?? "NOT ASSIGNED",
        };
      });

      setGroupCodePermissionsData(groupCodePermissionsMap);
      setEntityPermissionsData(enitityPermissionsMap);
    });
  };

  const onEditUser = (record: any) => {
    getUserPermissions(record.id);
    showDrawer(RowAction.Edit);
    setEmailInput(record.email);
    setUserTypeInput(record.isSocial ? UserType.AzureAD : UserType.Local);
    setUserNameInput(record.userName);
    setPasswordInput(record.password);
    setIsSocial(record.isSocial);
    setIsAdmin(record.isAdmin);
    setGroupsInput(record.userGroups);
    setDbUserGroups(record.userGroups.map((x) => x.id));
    setEditedRow(record);
    
  };

  const onDoubleClickRow = (record: any) => {
    getUserPermissions(record.id);
    showDrawer(RowAction.View);
    setEmailInput(record.email);
    setUserTypeInput(record.isSocial ? UserType.AzureAD : UserType.Local);
    setUserNameInput(record.userName);
    setPasswordInput(record.password);
    setIsSocial(record.isSocial);
    setIsAdmin(record.isAdmin);
    setGroupsInput(record.userGroups);
    setDbUserGroups(record.userGroups.map((x) => x.id));
    setEditedRow(record);
  };

  const onDeleteUser = (id: number) => {
    UsersService.remove(id).then((response: any) => {
      setIsTableLoading(true);
      deleteUserNotification("bottomRight");
    });
  };

  const onSaveUser = () => {
    setLoadingButton(true);

    if (editedRow?.id) {
      const model: UpdateUser = {
        Id: editedRow.id,
        Email: emailInput,
        UserName: userNameInput,
        Password: passwordInput,
        IsSocial: isSocial,
        IsAdmin: isAdmin,
        GroupIds: groupsInput.map((group) => group.id),
      };
      UsersService.update(model)
        .then((response: any) => {
          PermissionsService.upsert([...groupCodePermissionsData, ...entityPermissionsData])
            .catch((error: any) => {
              openFailedNotification(error.response.data, "bottomRight");
            })
            .finally(() => {
              setLoadingButton(false);
            });
          setIsTableLoading(true);
          onClose();
          setEmailInput("");
          setPasswordInput("");
          setUserTypeInput(UserType.Local);
          setIsAdmin(false);
          editUserNotification("bottomRight");
        })
        .catch((error: any) => {
          openFailedNotification(error.response.data, "bottomRight");
        })
        .finally(() => setLoadingButton(false));
    } else {
      const model: CreateUser = {
        Email: emailInput,
        UserName: userNameInput,
        Password: passwordInput,
        IsSocial: isSocial,
        IsAdmin: isAdmin,
        GroupIds: groupsInput.map((group) => group.id),
      };
      UsersService.create(model)
        .then((response: any) => {
          PermissionsService.upsert([
            ...groupCodePermissionsData.map((p) => ({
              ...p,
              objectId: response.data.id,
            })),
            ...entityPermissionsData.map((p) => ({
              ...p,
              objectId: response.data.id,
            })),
          ])
            .catch((error: any) => {
              openFailedNotification(error.response.data, "bottomRight");
            })
            .finally(() => {
              setLoadingButton(false);
            });
          setIsTableLoading(true);
          onClose();
          setEmailInput("");
          setPasswordInput("");
          setUserTypeInput(UserType.Local);
          setIsAdmin(false);
          addUserNotification("bottomRight");
        })
        .catch((error: any) => {
          openFailedNotification(error.response.data, "bottomRight");
        })
        .finally(() => setLoadingButton(false));
    }
  };

  const openFailedNotification = (description: string, placement: NotificationPlacement) => {
    notification.error({
      message: `Error`,
      description: description,
      placement,
    });
  };

  const addUserNotification = (placement: NotificationPlacement) => {
    notification.success({
      message: "User created successfully",
      placement,
      description: "",
      onClick: () => {
        console.log("Notification Clicked!");
      },
    });
  };

  const editUserNotification = (placement: NotificationPlacement) => {
    notification.success({
      message: "User edited successfully",
      placement,
      description: "",
      onClick: () => {
        console.log("Notification Clicked!");
      },
    });
  };

  const deleteUserNotification = (placement: NotificationPlacement) => {
    notification.success({
      message: "User deleted successfully",
      placement,
      description: "",
      onClick: () => {
        console.log("Notification Clicked!");
      },
    });
  };

  useEffect(() => {
    GroupCodesService.getAll().then((response: any) => {
      const data = response.data;
      setGroupCodes(data);
    });
    TablesService.getAllEntities().then((response: any) => {
      const data = response.data;
      setEntities(data);
    });
    TablesService.getAllOperations().then((response: any) => {
      const data = response.data;
      setOperations(data);
    });
  }, []);

  useEffect(() => {
    if (isTableLoading) {
      const promises = [UserGroupService.getAll(), UsersService.getAll()];

      const getData = async () => {
        const data = await Promise.allSettled(promises);
        const groups = data[0]["value"]["data"];
        const users = data[1]["value"]["data"];
        setGroups(groups);

        setData(
          users.map((x: any) => ({
            ...x,
            key: x.id,
          }))
        );
        setIsTableLoading(false);
      };

      getData();
    }
  }, [isTableLoading]);

  const onGroupChange = (groupIds: number[]) => {
    let groupsArr = groupIds.map((groupId) => {
      let group = groups.find((group) => group.id === groupId);
      return { ...group };
    });
    setGroupsInput(groupsArr);
    setUpdateRowAllowed(true);
  };

  useEffect(() => {
    if (editedRow && (userNameInput || isAdmin || isSocial)) {
      const def = data.find((x: any) => x.id === editedRow.id);
      if (
        userNameInput != def?.userName ||
        isAdmin != def?.isAdmin ||
        isSocial != def?.isSocial ||
        passwordInput! + def?.password
      ) {
        setUpdateRowAllowed(true);
      } else {
        setUpdateRowAllowed(false);
      }
    } else {
      setUpdateRowAllowed(false);
    }
  }, [userNameInput, isAdmin, isSocial, passwordInput, editedRow]);

  useEffect(() => {
    if (!groupCodePermissionsData.length && !entityPermissionsData.length) {
      return;
    }

    if (isAdmin) {
      setDbGroupCodePermissionsData(JSON.parse(JSON.stringify(groupCodePermissionsData)));
      setGroupCodePermissionsData(
        groupCodePermissionsData.map((p) => ({
          ...p,
          method: "DELETE",
        }))
      );

      setDbEntityPermissionsData(JSON.parse(JSON.stringify(entityPermissionsData)));
      setEntityPermissionsData(
        entityPermissionsData.map((e) => ({
          ...e,
          method: "DELETE",
        }))
      );
    } else {
      if (!!dbGroupCodePermissionsData.length) {
        setDbGroupCodePermissionsData([...dbGroupCodePermissionsData]);
      }

      if (!!dbEntityPermissionsData.length) {
        setEntityPermissionsData([...dbEntityPermissionsData]);
      }
    }
  }, [isAdmin]);

  const onEmailInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setEmailInput(event.target.value);
  };

  const onPasswordInputChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    setPasswordInput(event.target.value);
  };

  const onIsAdminChange = (event: CheckboxChangeEvent) => {
    setIsAdmin(event.target.checked);
  };

  useEffect(() => {
    if (userTypeInput === UserType.AzureAD) {
      setIsSocial(true);
    } else {
      setIsSocial(false);
    }
  }, [userTypeInput]);

  const onAddGroupClick = () => {
    if (selectedGroup) {
      setGroupsInput([...groupsInput, selectedGroup]);
      setSelectedGroup(null);
    }
  };

  const handleNewUserButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // setNewRuleOverviewModalEnabled(true);
    getUserPermissions(-1);
    showDrawer(RowAction.Create);
  };

  const showGroups = (groups: UserGroups[]) => {
    return (
      <div>
        {groups.map(function (d, idx) {
          return <li key={idx}>{d.name}</li>;
        })}
      </div>
    );
  };

  const userManagementActionButton = (
    <Button
      type={"primary"}
      onClick={handleNewUserButtonClick}
      className="actionButton"
      hidden={!editAllowed}
    >
      <PlusOutlined />
      NEW USER
    </Button>
  );

  const entitiesColumns: ColumnsType<UpsertPermission> = [
    {
      title: "Entity",
      dataIndex: "valueName",
      key: "valueName",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.valueName.localeCompare(b.valueName),
    },
    {
      title: "Permissions",
      dataIndex: "method",
      key: "method",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      render(value, record, index) {
        return (
          <Select
            allowClear
            showSearch
            style={{ width: "100%" }}
            placeholder="Please select"
            options={PERMISSION_METHODS_ARRAY
              .map(pm => {
                return {
                  label: pm,
                  value: pm,
                }
              })
              .sort((a,b) => a.value.localeCompare(b.value))
            }
            value={
              {
                label: record.method,
                value: record.method,
              }
            }
            onChange={(method) => onPermissionMethodChange(record, method)}
          />
        );
      },
      sorter: (a, b) => a.method.localeCompare(b.method),
    },
  ];

  const groupCodesColumns: ColumnsType<UpsertPermission> = [
    {
      title: "Group Code",
      dataIndex: "valueName",
      key: "valueName",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.valueName.localeCompare(b.valueName),
    },
    {
      title: "Permissions",
      dataIndex: "method",
      key: "method",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      render(value, record, index) {
        return (
          <Select
            allowClear
            showSearch
            style={{ width: "100%" }}
            placeholder="Please select"
            options={PERMISSION_METHODS_ARRAY
              .map(pm => {
                return {
                  label: pm,
                  value: pm,
                }
              })
              .sort((a,b) => a.value.localeCompare(b.value))
            }
            value={
              {
                label: record.method,
                value: record.method,
              }
            }
            onChange={(method) => onPermissionMethodChange(record, method)}
          />
        );
      },
      sorter: (a, b) => a.method.localeCompare(b.method),
    },
  ];

  const editGroupsColumns: ColumnsType<EditGroupsDataType> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      sorter: (a, b) => a.level.localeCompare(b.level),
    },
  ];

  const onPermissionMethodChange = (record: any, method: any) => {
    console.log(record);
    setUpdateRowAllowed(true);
    const permissions =
        record.node === "GROUPCODE"
        ? [...groupCodePermissionsData]
        : [...entityPermissionsData];
    const index = permissions.findIndex(
      (p) => p.node === record.node && p.valueId === record.valueId
    );
    const permission = { ...permissions[index] };
    permission.method = method;
    permissions[index] = permission;

    if (record.node === "GROUPCODE") {
      setGroupCodePermissionsData(permissions);
    } else {
      setEntityPermissionsData(permissions);
    }
  };

  const columns: ColumnsType<DataType> = [
    {
      title: "Display name",
      dataIndex: "userName",
      key: "userName",
      filters: [],
      filterMode: "tree",
      // filterSearch: true,
      // ...getColumnSearchProps(
      //   "ruleId",
      //   onRuleIdFilterChange,
      //   ruleIdFilterTreeData,
      //   ruleIdCheckedKeys,
      //   onRuleIdTreeItemCheck,
      //   ruleIdAscending,
      //   ruleIdDescending,
      //   setRuleIdAscending,
      //   setRuleIdDescending
      // ),
      sorter: (a, b) => a.userName.localeCompare(b.userName),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      filters: [],
      filterMode: "tree",
      // filterSearch: true,
      // ...getColumnSearchProps(
      //   "ruleId",
      //   onRuleIdFilterChange,
      //   ruleIdFilterTreeData,
      //   ruleIdCheckedKeys,
      //   onRuleIdTreeItemCheck,
      //   ruleIdAscending,
      //   ruleIdDescending,
      //   setRuleIdAscending,
      //   setRuleIdDescending
      // ),
      sorter: (a, b) => a.email.localeCompare(b.email),
    },
    {
      title: "Groups",
      dataIndex: "id",
      key: "id",
      filters: [],
      filterMode: "tree",
      render: (val, rec) => (
        <Popover content={() => showGroups(rec.userGroups)} title="User Groups">
          <Button icon={<GroupOutlined />} className={"active-btn"} size="small">
            Groups
          </Button>
        </Popover>
      ),
    },
    {
      title: "Created at",
      dataIndex: "sysTimeCreated",
      key: "sysTimeCreated",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "ruleName",
      //   onRuleNameFilterChange,
      //   ruleNameFilterTreeData,
      //   ruleNameCheckedKeys,
      //   onRuleNameTreeItemCheck,
      //   ruleNameAscending,
      //   ruleNameDescending,
      //   setRuleNameAscending,
      //   setRuleNameDescending
      // ),
      sorter: (a, b) => a.sysTimeCreated.localeCompare(b.sysTimeCreated),
    },
    {
      title: "Modified at",
      dataIndex: "sysTimeUpdated",
      key: "sysTimeUpdated",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "ruleName",
      //   onRuleNameFilterChange,
      //   ruleNameFilterTreeData,
      //   ruleNameCheckedKeys,
      //   onRuleNameTreeItemCheck,
      //   ruleNameAscending,
      //   ruleNameDescending,
      //   setRuleNameAscending,
      //   setRuleNameDescending
      // ),
      sorter: (a, b) => a.sysTimeUpdated.localeCompare(b.sysTimeUpdated),
    },
    {
      title: "Created by",
      dataIndex: "sysUserCreated",
      key: "sysUserCreated",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "ruleName",
      //   onRuleNameFilterChange,
      //   ruleNameFilterTreeData,
      //   ruleNameCheckedKeys,
      //   onRuleNameTreeItemCheck,
      //   ruleNameAscending,
      //   ruleNameDescending,
      //   setRuleNameAscending,
      //   setRuleNameDescending
      // ),
      sorter: (a, b) => a.sysTimeCreated.localeCompare(b.sysTimeCreated),
    },
    {
      title: "Modified by",
      dataIndex: "sysUserUpdated",
      key: "sysUserUpdated",
      filters: [],
      filterMode: "tree",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "ruleName",
      //   onRuleNameFilterChange,
      //   ruleNameFilterTreeData,
      //   ruleNameCheckedKeys,
      //   onRuleNameTreeItemCheck,
      //   ruleNameAscending,
      //   ruleNameDescending,
      //   setRuleNameAscending,
      //   setRuleNameDescending
      // ),
      sorter: (a, b) => a.sysTimeUpdated.localeCompare(b.sysTimeUpdated),
    },
    {
      title: "Is Social",
      dataIndex: "isSocial",
      key: "isSocial",
      filters: [],
      filterMode: "tree",
      width: "100px",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "ruleDescription",
      //   onRuleDescriptionFilterChange,
      //   ruleDescriptionFilterTreeData,
      //   ruleDescriptionCheckedKeys,
      //   onRuleDescriptionTreeItemCheck,
      //   ruleDescriptionAscending,
      //   ruleDescriptionDescending,
      //   setRuleDescriptionAscending,
      //   setRuleDescriptionDescending
      // ),
    },
    {
      title: "Is Admin",
      dataIndex: "isAdmin",
      key: "isAdmin",
      filters: [],
      filterMode: "tree",
      width: "100px",
      filterSearch: true,
      // ...getColumnSearchProps(
      //   "ruleDescription",
      //   onRuleDescriptionFilterChange,
      //   ruleDescriptionFilterTreeData,
      //   ruleDescriptionCheckedKeys,
      //   onRuleDescriptionTreeItemCheck,
      //   ruleDescriptionAscending,
      //   ruleDescriptionDescending,
      //   setRuleDescriptionAscending,
      //   setRuleDescriptionDescending
      // ),
    },
  ];

  const scroll = { y: "calc(100vh - 320px)"  };

  return (
    <div>
      <PageHead
        title={t("Page.Administration.UserManagement.Headers.UserManagement", {
          lngs: i18n.languages,
          defaultValue: "User management",
        })}
        icon={<UserSwitchOutlined />}
        routes={routes}
        groupCodes={groupCodes}
        entities={entities}
        operations={operations}
        selectedGroupCode={selectedGroupCode}
        selectedEntity={selectedEntity}
        selectedOperation={selectedOperation}
        setSelectedGroupCode={setSelectedGroupCode}
        setSelectedEntity={setSelectedEntity}
        setSelectedOperation={setSelectedOperation}
        setSelectGroupCodeDisabled={true}
        setSelectEntityDisabled={true}
        setSelectOperationDisabled={true}
        mainAction={userManagementActionButton}
        otherActions={[]}
        setActionsDisabled={false}
      />
      <div className="card-container">
        <Tabs
          type="card"
          defaultActiveKey="user-management"
          // activeKey={searchParams.get("tab") ? searchParams.get("tab")! : ""}
          onChange={(tab) => setActiveTab(tab)}
          style={{ height: "100%" }}
        >
          <TabPane
            tab={t("Page.Administration.UserManagement.Headers.UserManagement", {
              lngs: i18n.languages,
              defaultValue: "User management",
            })}
            key="user-management"
          >
            <Datatable
              scroll={scroll}
              tableColumns={columns}
              data={data}
              setData={setData}
              isTableLoading={isTableLoading}
              otherActions={[]}
              otherRowEnabled={false}
              editRowEnabled={editAllowed}
              copyRowEnabled={false}
              deleteRowEnabled={editAllowed}
              onEditFunc={onEditUser}
              onCopyFunc={undefined}
              onDeleteFunc={onDeleteUser}
              onDoubleClickRow={onDoubleClickRow}
              selectable={false}
              multiSelect={false}
              selectedRows={[]}
              setSelectedRows={undefined}
              showActionsColumn={editAllowed}
              paginationDisabled={true}
            ></Datatable>
            {openDrawer && (
                <Drawer
                    width={"400px"}
                    getContainer={false}
                    style={{ position: "absolute" }}
                    title={drawerTitle}
                    placement={placement}
                    onClose={onClose}
                    visible={openDrawer}
                    closable={false}
                    height={"90%"}
                    extra={
                      <Space>
                        {RowAction?.View === drawerAction && (
                            <>
                              <Button
                                  onClick={() => showDrawer(RowAction.Edit)}
                                  icon={<EditOutlined />}
                                  className={"active-btn"}
                                  size="small"
                                  disabled={!editAllowed}
                                  hidden={!editAllowed}
                              >
                                Edit
                              </Button>
                              <Button
                                  type="ghost"
                                  onClick={onClose}
                                  icon={<CloseOutlined />}
                                  size="small"
                              />
                            </>
                        )}
                        {RowAction?.Create === drawerAction && (
                            <>
                              <Button
                                  disabled={!emailInput && !isAdmin}
                                  className={!emailInput && !isAdmin ? "" : "active-btn"}
                                  onClick={onSaveUser}
                                  icon={<CheckOutlined />}
                                  size="small"
                                  loading={loadingButton}
                              >
                                Save
                              </Button>
                              <Button
                                  type="ghost"
                                  onClick={onClose}
                                  icon={<CloseOutlined />}
                                  size="small"
                              />
                            </>
                        )}
                        {RowAction?.Edit === drawerAction && (
                            <>
                              <Button
                                  disabled={!updateRowAllowed}
                                  className={!updateRowAllowed ? "" : "active-btn"}
                                  onClick={onSaveUser}
                                  icon={<CheckOutlined />}
                                  size="small"
                                  loading={loadingButton}
                              >
                                Update
                              </Button>
                              <Button
                                  type="ghost"
                                  onClick={onClose}
                                  icon={<CloseOutlined />}
                                  size="small"
                              />
                            </>
                        )}
                      </Space>
                    }
                >
                  <Form disabled={RowAction.View === drawerAction ? true : false} layout="vertical">
                    <div className={styles.columnsWrap}>
                      <div className={styles.leftColumn}>
                        <Form.Item label="Display Name:">
                          <Input
                              allowClear
                              value={userNameInput}
                              placeholder="enter value"
                              onChange={(e) => setUserNameInput(e.target.value)}
                          ></Input>
                        </Form.Item>
                        {/* <Form.Item label="User Type:">
                      <Input
                        allowClear
                        value={userTypeInput}
                        placeholder="enter value"
                        onChange={(e) => setUserTypeInput(e.target.value)}
                            ></Input> */}
                        <Form.Item label="User Type:">
                          <div
                              style={{
                                display: "flex",
                                flexDirection: "row",
                                justifyContent: "space-between",
                              }}
                          ></div>
                          <Select
                              allowClear
                              showSearch
                              style={{ width: "100%" }}
                              placeholder="Please select"
                              filterSort={(optionA, optionB) =>
                                  (optionA!.label as unknown as string)
                                      .toLowerCase()
                                      .localeCompare(
                                          (optionB!.label as unknown as string).toLowerCase()
                                      )
                              }
                              filterOption={(input, option) =>
                                  (option?.label ?? "")
                                      .toString()
                                      .toLowerCase()
                                      .includes(input.toLowerCase())
                              }
                              options={[
                                {
                                  label: "Local User",
                                  value: UserType.Local,
                                },
                                {
                                  label: "Azure AD",
                                  value: UserType.AzureAD,
                                },
                              ]}
                              value={userTypeInput}
                              onChange={(typeId) => setUserTypeInput(typeId)}
                          />
                        </Form.Item>
                        <Form.Item
                            label="Email:"
                            // hidden={userTypeInput !== UserType.Local}
                        >
                          <Input
                              allowClear
                              disabled={RowAction?.Create !== drawerAction}
                              value={emailInput}
                              placeholder="enter value"
                              onChange={(e) => setEmailInput(e.target.value)}
                          ></Input>
                        </Form.Item>
                        <Form.Item label="Password:" hidden={userTypeInput !== UserType.Local}>
                          <Input.Password
                              value={passwordInput}
                              placeholder="Password"
                              onChange={(e) => setPasswordInput(e.target.value)}
                          ></Input.Password>
                        </Form.Item>
                        {/* <Form.Item
                      label="Azure AD ID:"
                      hidden={userTypeInput !== UserType.AzureAD}
                    >
                      <Input
                        allowClear
                        // disabled={RowAction?.Create !== drawerAction}
                        value={azureAdId}
                        placeholder="enter value"
                        onChange={(e) => setAzureAdId(e.target.value)}
                      ></Input>
                    </Form.Item> */}
                        <Form.Item>
                          {/* <Checkbox
                        checked={isSocial}
                        onChange={(e) => setIsSocial(e.target.checked)}
                      >
                        Is Social
                      </Checkbox> */}
                          <Checkbox checked={isAdmin} onChange={onIsAdminChange}>
                            Is Admin
                          </Checkbox>
                        </Form.Item>
                      </div>
                      <div className={styles.rightColumn}>
                        <Collapse bordered={false}>
                          <Panel
                              header="Entity permissions"
                              key="1"
                              style={{ backgroundColor: "white" }}
                          >
                            <Datatable
                                scroll={scroll}
                                tableColumns={entitiesColumns}
                                data={entityPermissionsData}
                                setData={setEntityPermissionsData}
                                isTableLoading={isTableLoading}
                                otherActions={[]}
                                otherRowEnabled={false}
                                editRowEnabled={false}
                                copyRowEnabled={false}
                                deleteRowEnabled={false}
                                onEditFunc={undefined}
                                onCopyFunc={undefined}
                                onDeleteFunc={undefined}
                                onDoubleClickRow={() => {}}
                                selectable={false}
                                multiSelect={false}
                                selectedRows={[]}
                                setSelectedRows={undefined}
                                showActionsColumn={false}
                            ></Datatable>
                          </Panel>
                          <Panel
                              header="Group Code permissions"
                              key="2"
                              style={{ backgroundColor: "white" }}
                          >
                            <Datatable
                                scroll={scroll}
                                tableColumns={groupCodesColumns}
                                data={groupCodePermissionsData}
                                setData={setGroupCodePermissionsData}
                                isTableLoading={isTableLoading}
                                otherActions={[]}
                                otherRowEnabled={false}
                                editRowEnabled={editAllowed}
                                copyRowEnabled={false}
                                deleteRowEnabled={false}
                                onEditFunc={undefined}
                                onCopyFunc={undefined}
                                onDeleteFunc={undefined}
                                onDoubleClickRow={() => {}}
                                selectable={false}
                                multiSelect={false}
                                selectedRows={[]}
                                setSelectedRows={undefined}
                                showActionsColumn={false}
                            ></Datatable>
                          </Panel>
                        </Collapse>
                        {/* <Form.Item label="Add to Group:">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between",
                        }}
                      >
                        <Select
                          allowClear
                          showSearch
                          style={{ width: "100%" }}
                          placeholder="Please select"
                          filterOption={(input, option) =>
                            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
                          }
                          options={groups
                            .filter(u => !groupsInput
                              .find(ui => (ui.id === u.id)))
                            .map(u => {
                              return {
                                label: u.name,
                                value: u.id,
                              }
                            })}
                          value={
                            {
                              label: selectedGroup?.name,
                              value: selectedGroup?.id
                            }
                          }
                          onChange={(groupId) => setSelectedGroup(groups.find(g => g.id === groupId))}
                      />
                        <Button
                          icon={<UsergroupAddOutlined/>}
                          type="primary"
                          className={styles.green_btn}
                          onClick={() => onAddGroupClick()}
                        />
                      </div>
                    </Form.Item>
                    <Form.Item label="Edit Groups:">
                      <Datatable
                        scroll={scroll}
                        tableColumns={editGroupsColumns}
                        data={groupsInput}
                        setData={setGroupsInput}
                        isTableLoading={isTableLoading}
                        otherActions={[]}
                        otherRowEnabled={false}
                        editRowEnabled={false}
                        copyRowEnabled={false}
                        deleteRowEnabled={true}
                        onEditFunc={undefined}
                        onCopyFunc={undefined}
                        onDeleteFunc={onDeleteGroup}
                        onDoubleClickRow={onDoubleClickRow}
                        selectable={false}
                        multiSelect={false}
                        selectedRows={[]}
                        setSelectedRows={undefined}
                      ></Datatable>
                    </Form.Item> */}
                      </div>
                    </div>
                  </Form>
                </Drawer>  
            )}
          </TabPane>
          {/* <TabPane
            tab={t("Page.Administration.UserManagement.Headers.AccessManagement", {
              lngs: i18n.languages,
              defaultValue: "Access management",
            })}
            key="access-management"
          ></TabPane> */}
        </Tabs>
      </div>
    </div>
  );
};

export default UserManagement;
